/**
 * Log redaction.
 *
 * The compliance requirements running through this platform make log content
 * a correctness concern, not a tidiness one:
 *
 * - PCI DSS forbids PAN, CVV or track data appearing in logs, in any mode,
 *   including debug (Task 16).
 * - GDPR Art. 5 data minimisation and the audit-metadata rule in Task 02
 *   forbid dumping guest personal data into log or audit payloads.
 *
 * Redaction is therefore applied by default in the logger factory rather than
 * left to each call site to remember.
 */
/**
 * Field paths pino redacts wholesale.
 *
 * Both `field` and `*.field` forms are listed: pino path matching is literal,
 * so a wildcard is needed to catch nested occurrences.
 */
export const REDACTED_PATHS = [
    // Credentials and secrets
    'password',
    '*.password',
    'passwordHash',
    '*.passwordHash',
    'currentPassword',
    '*.currentPassword',
    'newPassword',
    '*.newPassword',
    'secret',
    '*.secret',
    'apiKey',
    '*.apiKey',
    'credentials',
    '*.credentials',
    // Tokens and session material
    'token',
    '*.token',
    'accessToken',
    '*.accessToken',
    'refreshToken',
    '*.refreshToken',
    'authorization',
    '*.authorization',
    'cookie',
    '*.cookie',
    'req.headers.authorization',
    'req.headers.cookie',
    'headers.authorization',
    'headers.cookie',
    // Cardholder data. These must never reach a log line under any
    // circumstances; the fields are listed defensively because an upstream
    // gateway payload has historically been the way they leak.
    'cardNumber',
    '*.cardNumber',
    'pan',
    '*.pan',
    'cvv',
    '*.cvv',
    'cvc',
    '*.cvc',
    'expiryDate',
    '*.expiryDate',
    'cardHolderName',
    '*.cardHolderName',
    // Guest personal data
    'email',
    '*.email',
    'phone',
    '*.phone',
    'dateOfBirth',
    '*.dateOfBirth',
    'documentNumber',
    '*.documentNumber',
    'address',
    '*.address',
];
export const REDACTION_PLACEHOLDER = '[REDACTED]';
/**
 * Matches anything shaped like a card number: 13-19 digits, optionally split
 * by spaces or hyphens.
 */
const PAN_PATTERN = /\b(?:\d[ -]?){13,19}\b/gu;
/**
 * Scrubs PAN-shaped substrings from free text.
 *
 * Path-based redaction cannot help when a card number is embedded in a
 * message string or a third-party error. This is a backstop for that case —
 * not a licence to log payloads and rely on it.
 */
export function scrubPanLikeText(value) {
    return value.replace(PAN_PATTERN, (candidate) => {
        const digits = candidate.replace(/\D/gu, '');
        return isLuhnValid(digits) ? REDACTION_PLACEHOLDER : candidate;
    });
}
const LUHN_DOUBLE_THRESHOLD = 9;
const LUHN_ADJUSTMENT = 9;
const LUHN_MODULUS = 10;
const LUHN_DOUBLE_FACTOR = 2;
/** ISO/IEC 7812 bounds on a primary account number. */
const MIN_PAN_DIGITS = 13;
const MAX_PAN_DIGITS = 19;
/**
 * Luhn check.
 *
 * Applied so that ordinary long digit strings — booking references, invoice
 * numbers, correlation IDs — are not mangled. A false positive that redacts a
 * booking reference makes logs useless; a false negative leaks a card number.
 * Luhn keeps both rare.
 */
export function isLuhnValid(digits) {
    if (digits.length < MIN_PAN_DIGITS || digits.length > MAX_PAN_DIGITS)
        return false;
    let sum = 0;
    let double = false;
    for (let index = digits.length - 1; index >= 0; index -= 1) {
        const digit = Number(digits[index]);
        if (Number.isNaN(digit))
            return false;
        if (double) {
            const doubled = digit * LUHN_DOUBLE_FACTOR;
            sum += doubled > LUHN_DOUBLE_THRESHOLD ? doubled - LUHN_ADJUSTMENT : doubled;
        }
        else {
            sum += digit;
        }
        double = !double;
    }
    return sum % LUHN_MODULUS === 0;
}
//# sourceMappingURL=redaction.js.map